import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoFiltersCard from "../../../components/eco-ui/EcoFiltersCard";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import { useGoBack } from "../../../hooks/useGoBack";

import { listCategoriesApi, listEntriesApi } from "../api/financeiroApi";
import {
  ENTRY_SOURCE_LABELS,
  ENTRY_STATUS_BADGE,
  FinEntry,
  FinEntryList,
  FinEntryStatus,
  FinEntryType,
  KIND_FOR_TYPE,
  isCompetenceOnlySource,
  isDpSource,
  statusLabel,
} from "../api/finTypes";
import SettlementModal from "../components/SettlementModal";
import CostCenterSelect from "../components/CostCenterSelect";
import { useCostCenters } from "../utils/costCenters";
import { FINANCEIRO_PAGE, FIN_ENTRIES_PAGE, FIN_ENTRY_NEW_PAGE, FIN_ENTRY_PAGE } from "../routes";
import { formatCurrency, formatDateOnly } from "../../dp/utils/format";
import { CategoryOption, ENTRY_ORIGINS as ORIGINS, flattenCategories, monthRange, toYmd, todayYmd, useFinPermissions } from "../utils/fin";
import "../../dp/pages/Dp.css";
import "../../dp/pages/DpEco.css";
import "./Financeiro.css";

const PAGE_SIZE = 20;

/** Situações oferecidas no filtro (valores da URL, `?situacao=`). */
const STATUS_PRESETS = {
  abertos: { label: "Em aberto", statuses: ["PENDENTE", "ATRASADO", "PARCIAL"] as FinEntryStatus[] },
  atrasados: { label: "Vencidos", statuses: ["ATRASADO", "PARCIAL"] as FinEntryStatus[] },
  pagos: { label: "Quitados", statuses: ["PAGO"] as FinEntryStatus[] },
  cancelados: { label: "Cancelados", statuses: ["CANCELADO"] as FinEntryStatus[] },
  todos: { label: "Todas as situações", statuses: [] as FinEntryStatus[] },
};
type StatusPreset = keyof typeof STATUS_PRESETS;

/** Períodos de vencimento (valores da URL, `?periodo=`). */
const PERIODS = {
  mes: "Este mês",
  anterior: "Mês anterior",
  proximo: "Próximo mês",
  hoje: "Hoje",
  "7dias": "Próximos 7 dias",
  todos: "Qualquer data",
};
type Period = keyof typeof PERIODS;

const addDaysYmd = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toYmd(d);
};

function periodRange(period: Period): { dueFrom?: string; dueTo?: string } {
  switch (period) {
    case "anterior": {
      const r = monthRange(new Date(), -1);
      return { dueFrom: r.from, dueTo: r.to };
    }
    case "proximo": {
      const r = monthRange(new Date(), 1);
      return { dueFrom: r.from, dueTo: r.to };
    }
    case "hoje":
      return { dueFrom: todayYmd(), dueTo: todayYmd() };
    case "7dias":
      return { dueFrom: todayYmd(), dueTo: addDaysYmd(7) };
    case "todos":
      return {};
    default: {
      const r = monthRange();
      return { dueFrom: r.from, dueTo: r.to };
    }
  }
}

type Props = { type: FinEntryType };

/** Contas a pagar ou a receber: filtros na URL, para o Voltar e os links do painel. */
export default function EntriesPage({ type }: Props) {
  const navigate = useNavigate();
  const goBack = useGoBack(FINANCEIRO_PAGE());
  const { token, canWrite } = useFinPermissions();
  const [params, setParams] = useSearchParams();

  const status = (params.get("situacao") as StatusPreset) in STATUS_PRESETS ? (params.get("situacao") as StatusPreset) : "abertos";
  const period = (params.get("periodo") as Period) in PERIODS ? (params.get("periodo") as Period) : "mes";
  const categoryId = params.get("categoria") ?? "";
  /** UUID do centro, ou "sem" (sem centro de custo e sem rateio). */
  const costCenterId = params.get("centro") ?? "";
  const groupId = params.get("grupo") ?? "";
  const origin = (params.get("origem") ?? "") in ORIGINS ? (params.get("origem") as string) : "";
  const q = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("pagina")) || 1);

  const [qInput, setQInput] = useState(q);
  const [list, setList] = useState<FinEntryList | null>(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const { costCenters, groups } = useCostCenters(token);
  const [settling, setSettling] = useState<FinEntry | null>(null);

  const isReceivable = type === "RECEBER";

  const setFilter = useCallback(
    (key: string, value: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          if (key !== "pagina") next.delete("pagina");
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  useEffect(() => setQInput(q), [q]);

  useEffect(() => {
    if (!token) return;
    listCategoriesApi(token, { kind: KIND_FOR_TYPE[type] })
      .then((tree) => setCategories(flattenCategories(tree)))
      .catch(() => setCategories([]));
  }, [token, type]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const range = periodRange(period);
      // "Vencidos": até ontem, para o parcial ainda no prazo não entrar.
      if (status === "atrasados") {
        const yesterday = addDaysYmd(-1);
        if (!range.dueTo || range.dueTo > yesterday) range.dueTo = yesterday;
      }
      const data = await listEntriesApi(token, {
        type,
        status: STATUS_PRESETS[status].statuses,
        ...range,
        categoryId: categoryId || undefined,
        costCenterId: costCenterId === "sem" ? "none" : costCenterId || undefined,
        groupId: groupId || undefined,
        source: origin ? ORIGINS[origin].sources : undefined,
        q: q || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setList(data);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao listar lançamentos.");
    } finally {
      setLoading(false);
    }
  }, [token, type, status, period, categoryId, costCenterId, groupId, origin, q, page]);

  useEffect(() => {
    load();
  }, [load]);

  const activeFilters =
    (status !== "abertos" ? 1 : 0) + (period !== "mes" ? 1 : 0) + (categoryId ? 1 : 0) + (costCenterId ? 1 : 0) + (groupId ? 1 : 0) + (origin ? 1 : 0) + (q ? 1 : 0);

  const summary = useMemo(
    () => `${STATUS_PRESETS[status].label} · ${PERIODS[period]}`,
    [status, period]
  );

  const title = isReceivable ? "Contas a receber" : "Contas a pagar";

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[{ label: "Financeiro", onClick: () => navigate(FINANCEIRO_PAGE()) }, { label: title }]}
          title={title}
          subtitle={
            isReceivable
              ? "O que a empresa tem a receber, por vencimento."
              : "O que a empresa tem a pagar, por vencimento."
          }
          onBack={goBack}
          backLabel="Voltar ao Financeiro"
          actions={
            canWrite ? (
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => navigate(FIN_ENTRY_NEW_PAGE(type))}>
                <EcoIcon name="plus" size={18} />
                {isReceivable ? "Nova conta a receber" : "Nova conta a pagar"}
              </button>
            ) : (
              <span className="ecoPill ecoPill--off">Somente leitura</span>
            )
          }
        />

        <EcoSegmented<FinEntryType>
          label="Tipo"
          options={[
            { value: "PAGAR", label: "A pagar" },
            { value: "RECEBER", label: "A receber" },
          ]}
          value={type}
          onChange={(next) => navigate(`${FIN_ENTRIES_PAGE(next)}?${params.toString()}`, { replace: true })}
        />

        <EcoFiltersCard summary={summary} activeCount={activeFilters}>
          <div className="ecoFilters__grid">
            <div className="ecoField">
              <label htmlFor="f-sit">Situação</label>
              <select
                id="f-sit"
                className="ecoSelect ecoSelect--sm"
                value={status}
                onChange={(e) => setFilter("situacao", e.target.value === "abertos" ? "" : e.target.value)}
              >
                {(Object.keys(STATUS_PRESETS) as StatusPreset[]).map((key) => (
                  <option key={key} value={key}>
                    {STATUS_PRESETS[key].label}
                  </option>
                ))}
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="f-per">Vencimento</label>
              <select
                id="f-per"
                className="ecoSelect ecoSelect--sm"
                value={period}
                onChange={(e) => setFilter("periodo", e.target.value === "mes" ? "" : e.target.value)}
              >
                {(Object.keys(PERIODS) as Period[]).map((key) => (
                  <option key={key} value={key}>
                    {PERIODS[key]}
                  </option>
                ))}
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="f-cat">Categoria</label>
              <select
                id="f-cat"
                className="ecoSelect ecoSelect--sm"
                value={categoryId}
                onChange={(e) => setFilter("categoria", e.target.value)}
              >
                <option value="">Todas</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            {groups.length > 0 ? (
              <div className="ecoField">
                <label htmlFor="f-grp">Grupo de centro de custo</label>
                <select
                  id="f-grp"
                  className="ecoSelect ecoSelect--sm"
                  value={groupId}
                  onChange={(e) => setFilter("grupo", e.target.value)}
                >
                  <option value="">Todos</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.code} — {g.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="ecoField">
              <label htmlFor="f-cc">Centro de custo</label>
              <CostCenterSelect
                id="f-cc"
                className="ecoSelect ecoSelect--sm"
                value={costCenterId}
                onChange={(v) => setFilter("centro", v)}
                costCenters={costCenters}
                groups={groups}
                placeholder="Todos"
                extraOptions={[{ value: "sem", label: "Sem centro de custo" }]}
              />
            </div>
            <div className="ecoField">
              <label htmlFor="f-orig">Origem</label>
              <select
                id="f-orig"
                className="ecoSelect ecoSelect--sm"
                value={origin}
                onChange={(e) => setFilter("origem", e.target.value)}
              >
                <option value="">Todas</option>
                {Object.entries(ORIGINS).map(([key, o]) => (
                  <option key={key} value={key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="f-q">Buscar</label>
              <input
                id="f-q"
                className="ecoInput ecoInput--sm"
                placeholder={isReceivable ? "Descrição ou cliente" : "Descrição ou fornecedor"}
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onBlur={() => qInput.trim() !== q && setFilter("q", qInput.trim())}
                onKeyDown={(e) => e.key === "Enter" && setFilter("q", qInput.trim())}
              />
            </div>
          </div>
          {activeFilters > 0 ? (
            <div className="ecoFilters__row">
              <button type="button" className="ecoBtn ecoFilters__clear" onClick={() => setParams({}, { replace: true })}>
                Limpar
              </button>
            </div>
          ) : null}
        </EcoFiltersCard>

        {list ? (
          <div className="finTotals" aria-live="polite">
            <span>
              {list.totals.count} {list.totals.count === 1 ? "título" : "títulos"}
            </span>
            <span>
              Total <strong>{formatCurrency(list.totals.amount)}</strong>
            </span>
            <span>
              {isReceivable ? "Recebido" : "Pago"} <strong>{formatCurrency(list.totals.settled)}</strong>
            </span>
            <span>
              Em aberto <strong>{formatCurrency(list.totals.open)}</strong>
            </span>
          </div>
        ) : null}

        <section className="ecoCard">
          <div className="ecoTableWrap">
            <table className="ecoTable finTable finTable--cards">
              <thead>
                <tr>
                  <th>Vencimento</th>
                  <th>Descrição</th>
                  <th>Categoria</th>
                  <th className="ecoTable__num">Valor</th>
                  <th className="ecoTable__num">Em aberto</th>
                  <th>Situação</th>
                  <th className="ecoTable__num">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && !list ? (
                  <tr>
                    <td colSpan={7} className="ecoEmpty">
                      Carregando...
                    </td>
                  </tr>
                ) : !list || list.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="ecoEmpty">
                      Nenhum título com esses filtros.
                    </td>
                  </tr>
                ) : (
                  list.items.map((e) => (
                    <tr
                      key={e.id}
                      className={`${e.isOverdue ? "finRow--late" : ""} ${e.status === "CANCELADO" ? "finRow--canceled" : ""}`}
                    >
                      <td data-label="Vencimento">{formatDateOnly(e.dueDate)}</td>
                      <td data-label="">
                        <span className="finDesc">
                          <span className="finDesc__main">
                            {e.description}
                            {e.installmentNumber ? ` (${e.installmentNumber}/${e.installmentCount})` : ""}
                          </span>
                          <span className="finDesc__sub">
                            {isDpSource(e.source) ? (
                              <span className="dpBadge dpBadge--blue finSourceBadge">{ENTRY_SOURCE_LABELS[e.source]} · DP</span>
                            ) : null}
                            {e.allocations.length ? (
                              <span className="dpBadge dpBadge--gray finSourceBadge" title={e.allocations.map((a) => `${a.costCenter.code} ${a.costCenter.name}`).join(", ")}>
                                Rateado em {e.allocations.length}
                              </span>
                            ) : !e.costCenter ? (
                              <span className="dpBadge dpBadge--orange finSourceBadge">Sem centro de custo</span>
                            ) : null}
                            {e.counterparty?.name ??
                              (e.source === "RECORRENCIA"
                                ? "Repetição"
                                : isDpSource(e.source)
                                  ? null
                                  : "Sem fornecedor/cliente")}
                          </span>
                        </span>
                      </td>
                      <td data-label="Categoria" className="ecoTable__muted">{e.category?.name ?? "—"}</td>
                      <td data-label="Valor" className="ecoTable__num finMoney">{formatCurrency(e.amount)}</td>
                      <td data-label="Em aberto" className="ecoTable__num finMoney">{formatCurrency(e.openAmount)}</td>
                      <td data-label="Situação">
                        <span className={`dpBadge ${ENTRY_STATUS_BADGE[e.status]}`}>{statusLabel(e.status, e.type)}</span>
                      </td>
                      <td data-label="" className="ecoTable__num">
                        <div className="dpTable__actions" style={{ justifyContent: "flex-end" }}>
                          {canWrite && Number(e.openAmount) > 0 && !isCompetenceOnlySource(e.source) ? (
                            <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--primary" onClick={() => setSettling(e)}>
                              {isReceivable ? "Receber" : "Pagar"}
                            </button>
                          ) : null}
                          <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => navigate(FIN_ENTRY_PAGE(e.id))}>
                            Abrir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {list && list.total > PAGE_SIZE ? (
            <EcoPagination
              page={page}
              pageSize={PAGE_SIZE}
              total={list.total}
              onChange={(next) => setFilter("pagina", next > 1 ? String(next) : "")}
              disabled={loading}
            />
          ) : null}
        </section>
      </main>

      {settling ? (
        <SettlementModal
          token={token}
          entry={settling}
          onClose={() => setSettling(null)}
          onSaved={() => {
            setSettling(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
